import React, { useEffect, useRef, useState } from 'react';
import { HeroMobileApplication } from './hero-mobile-application';
import { HeroApplication } from './hero-application';
import { cn } from '../../../../../utils/tailwind';
import styles from './homepage-browser-preview.module.css';

interface HomepageBrowserPreviewProps {
    className?: string;
}

/** Browser and responsive sample application share one drawing sequence. */
export const HomepageBrowserPreview: React.FC<HomepageBrowserPreviewProps> = ({ className }) => {
    const previewRef = useRef<HTMLDivElement>(null);
    const [playing, setPlaying] = useState(false);
    useEffect(() => {
        const element = previewRef.current;
        if (!element) return undefined;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setPlaying(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.15 },
        );
        observer.observe(element);
        return () => observer.disconnect();
    }, []);
    return (
        <div
            ref={previewRef}
            data-playing={playing}
            id="ukazka-monery"
            tabIndex={-1}
            className={cn(styles.browser, className)}
        >
            <div className={styles.browserSurface}>
                <HeroApplication />
                <svg aria-hidden="true" className={styles.canvas}>
                    <rect
                        className={styles.outline}
                        x="1"
                        y="1"
                        rx="20"
                        fill="none"
                        stroke="#d9cdc5"
                        strokeWidth="1.5"
                        pathLength="1"
                    />
                    <line className={styles.divider} x1="1" x2="100%" y1="33" y2="33" stroke="#d9cdc5" pathLength="1" />
                    {[20, 32, 44].map((x, index) => (
                        <circle
                            key={x}
                            className={styles.dot}
                            style={{ animationDelay: `calc(${1.3 + index * 0.12}s * var(--hero-motion-scale))` }}
                            cx={x}
                            cy="18"
                            r="3.5"
                            fill="#b9aea6"
                        />
                    ))}
                    <text
                        className={styles.addressText}
                        x="50%"
                        y="21"
                        textAnchor="middle"
                        fill="#8c8178"
                        fontSize="10"
                        fontFamily="inherit"
                    >
                        monery.io
                    </text>
                </svg>
            </div>
            <svg className={styles.phone} viewBox="0 0 220 440" fill="none" aria-hidden="true">
                <rect className={styles.phoneSurface} x="3" y="3" width="214" height="434" rx="38" />
                <rect className={styles.phoneOutline} x="3" y="3" width="214" height="434" rx="38" pathLength="1" />
                <rect className={styles.phoneScreen} x="8" y="8" width="204" height="424" rx="33" pathLength="1" />
                <path className={styles.phoneButtons} d="M1 76V91 M1 108V135 M1 149V176 M219 117V160" pathLength="1" />
                <HeroMobileApplication />
                <rect className={styles.phoneIsland} x="77" y="19" width="66" height="19" rx="9.5" />
                <circle className={styles.phoneCamera} cx="133" cy="28.5" r="3" />
                <path className={styles.phoneDetails} d="M78 422H142" pathLength="1" />
            </svg>
        </div>
    );
};
